import { twApi } from './../api';
import { BaseQueryParams } from './../../Global.model';

import {
  FactoringStatus,
  INVOICE_FACTORING_STATUS_TYPE,
  InvoiceCreateData,
  InvoiceResponseV2,
  InvoiceResponseV3,
  ISearchInvoiceParams
} from '../../../providers/Invoices/Invoices.model';
import { InvoiceSummaryResponse } from '../../../providers/Invoices/Invoices.model';

/**
 * Api endpoints for invoice entity
 */
const invoiceApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    searchInvoices: builder.query<
      { content: InvoiceSummaryResponse[]; totalItems: number },
      { filterParams?: ISearchInvoiceParams; queryParams?: BaseQueryParams }
    >({
      query: ({ filterParams, queryParams }) => {
        return {
          url: '/v3/invoices/search',
          method: 'POST',
          body: filterParams,
          params: queryParams
        };
      },
      transformResponse: (response: { data: InvoiceSummaryResponse[]; total: number }) => {
        return {
          content: response.data,
          totalItems: response.total
        };
      },
      transformErrorResponse: (response) => {
        console.error('Invoice API Error:', response);
        return response;
      },
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Invoice' as const, id })),
              { type: 'Invoice', id: 'INVOICES_LIST' }
            ]
          : [{ type: 'Invoice', id: 'INVOICES_LIST' }]
    }),

    markAsPaid: builder.mutation<{ message: string }, { id: string; orderId?: string }>({
      query: ({ id, orderId }) => ({
        url: `v3/invoices/${id}/mark-as-paid`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, { id, orderId }) => [
        { type: 'Orders', id: orderId },
        { type: 'Orders', id: 'ORDER_LIST' },
        { type: 'Invoice', id },
        { type: 'Invoice', id: 'INVOICES_LIST' }
      ]
    }),
    markAsUnpaid: builder.mutation<{ message: string }, { id: string; orderId?: string }>({
      query: ({ id, orderId }) => ({
        url: `v3/invoices/${id}/mark-as-unpaid`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, { id, orderId }) => [
        { type: 'Orders', id: orderId },
        { type: 'Orders', id: 'ORDER_LIST' },
        { type: 'Invoice', id },
        { type: 'Invoice', id: 'INVOICES_LIST' }
      ]
    }),

    postExport: builder.mutation<
      { message: string; createdInvoices: InvoiceResponseV2[] },
      {
        data: InvoiceCreateData | InvoiceResponseV3 | InvoiceResponseV3[];
        download?: boolean;
        optimisticFactoringStatus?: FactoringStatus;
        searchInvoiceParams: { filterParams?: ISearchInvoiceParams; queryParams?: BaseQueryParams };
      }
    >({
      query: ({ data, download }) => ({
        url: download ? `/factoring/export?download=true` : `/factoring/export`,
        method: 'POST',
        body: data
      }),
      async onQueryStarted({ data, searchInvoiceParams, optimisticFactoringStatus }, { dispatch, queryFulfilled }) {
        // Handle array or single invoice data
        const invoices = Array.isArray(data) ? data : [data];

        // Get all invoice IDs
        const invoiceIds = invoices
          .map((invoice) => ('id' in invoice ? invoice.id : undefined))
          .filter((id): id is string => id !== undefined);

        // Optimistically update each invoice's factoring status to processing for quick feedback
        const patchResults = invoiceIds.map((id) =>
          dispatch(
            invoiceApi.util.updateQueryData(
              'searchInvoices',
              {
                filterParams: searchInvoiceParams.filterParams,
                queryParams: searchInvoiceParams.queryParams
              },
              (draft) => {
                const invoice = draft.content.find((inv) => inv.id === id);
                if (invoice && optimisticFactoringStatus) {
                  invoice.factoring_status = optimisticFactoringStatus;
                }
              }
            )
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // Rollback optimistic updates on error
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      }
      // Not invalidating invoice list because we want to keep the optimistic updates and we will manually fetch with some delay
    })
  })
});

export const {
  useSearchInvoicesQuery,
  useMarkAsPaidMutation,
  useMarkAsUnpaidMutation,
  usePostExportMutation
} = invoiceApi;
